/**
 * Точка монтирования моста в клиентском модуле. Рендерит `null` — это
 * side-effect компонент: при монтировании поднимает мост (`setupBridge`),
 * вешает обработку аппаратной «назад» и репортит `canGoBack` нативе при смене
 * роута. Должен находиться внутри роутера (react-router).
 *
 * В обычном браузере — полностью no-op (гейт `isInWebView`).
 */
import './webview.scss';

import { memo, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useAppDispatch } from '@/store';
import { startLoading, stopLoading } from '@/store/app.slice';

import { isInWebView } from './env';
import { setupBridge } from './setup';
import { SYSTEM_ACTIONS, SYSTEM_EVENTS, bridge } from './vendor/web';

interface IProps {
  children: React.ReactNode;
}

/** react-router хранит индекс позиции в истории в `history.state.idx`. */
const canGoBackInApp = (): boolean =>
  ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

const WebViewBridgeProvider: React.FC<IProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [isSetupFinished, setIsSetupFinished] = useState(false);

  // Поднятие моста + аппаратная «назад» (один раз за жизнь модуля).
  useEffect(() => {
    if (!isInWebView()) {
      setIsSetupFinished(true);
      return;
    }

    dispatch(startLoading());
    const teardownSetup = setupBridge(() => {
      setIsSetupFinished(true);
      dispatch(stopLoading());
    });
    const offBack = bridge.on(SYSTEM_EVENTS.BackPressed, () => {
      if (canGoBackInApp()) navigate(-1);
      // иначе native сам закроет экран мини-аппа — мы шлём canGoBack=false ниже.
    });

    return () => {
      offBack();
      teardownSetup();
    };
  }, []);

  // Сообщаем нативе, может ли web сам обработать «назад» — при каждой смене роута.
  useEffect(() => {
    if (!isInWebView()) return;
    bridge.send(SYSTEM_ACTIONS.NavigationSetCanGoBack, {
      value: canGoBackInApp()
    });

    // Убираем языковой префикс из пути
    // Событие привязано к iframe веб-оболочки (в нативных SYSTEM_EVENTS события нет)
    const pathname = location.pathname.replace(/^\/\w\w/, '');
    const fullPath = `${pathname}${location.search}`;
    bridge.send('navigation.routeChanged', { path: fullPath });
  }, [location.key]);

  return isSetupFinished ? children : null;
};

export default memo(WebViewBridgeProvider);
